import { ChevronRight } from 'lucide-react';
import { DeltaBadge } from './analyticsDelta';
import AnalyticsInfoHint from './AnalyticsInfoHint';

const TONE = {
  green: {
    iconWrap: 'bg-emerald-100 text-emerald-700',
    footer: 'bg-emerald-50 text-emerald-900 border-emerald-100',
    ring: 'focus-visible:ring-emerald-400',
  },
  blue: {
    iconWrap: 'bg-blue-100 text-blue-700',
    footer: 'bg-blue-50 text-blue-900 border-blue-100',
    ring: 'focus-visible:ring-blue-400',
  },
  purple: {
    iconWrap: 'bg-violet-100 text-violet-700',
    footer: 'bg-violet-50 text-violet-900 border-violet-100',
    ring: 'focus-visible:ring-violet-400',
  },
  orange: {
    iconWrap: 'bg-orange-100 text-orange-700',
    footer: 'bg-orange-50 text-orange-900 border-orange-100',
    ring: 'focus-visible:ring-orange-400',
  },
  sky: {
    iconWrap: 'bg-sky-100 text-sky-700',
    footer: 'bg-sky-50 text-sky-900 border-sky-100',
    ring: 'focus-visible:ring-sky-400',
  },
};

/**
 * Card KPI cliquable — le « i » n’est pas un bouton imbriqué dans le bouton de navigation.
 */
export default function AnalyticsClickableKpiCard({
  title,
  helpText,
  value,
  suffix,
  subText,
  deltaPct,
  icon: Icon,
  tone = 'green',
  footerLabel = 'Voir le détail',
  footerHint,
  onOpen,
  'data-testid': testId,
}) {
  const t = TONE[tone] || TONE.green;

  return (
    <div
      data-testid={testId || 'analytics-kpi-card'}
      className="group relative w-full text-left bg-white rounded-xl border border-gray-200 shadow-sm p-5 transition hover:border-gray-300 hover:shadow-md hover:-translate-y-0.5"
    >
      <div className="relative z-10 flex items-start justify-between gap-3 pointer-events-none">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-medium text-gray-600">{title}</p>
            {helpText ? <AnalyticsInfoHint text={helpText} label={`Définition ${title}`} /> : null}
          </div>
          <p className="mt-1 text-2xl font-bold text-gray-900 tracking-tight">
            {value}
            {suffix ? (
              <span className="text-lg font-semibold text-gray-500">{suffix}</span>
            ) : null}
          </p>
          {subText ? <p className="mt-0.5 text-xs text-gray-500">{subText}</p> : null}
          <div className="mt-1.5">
            <DeltaBadge value={deltaPct} />
          </div>
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          {Icon ? (
            <div className={`p-2.5 rounded-full ${t.iconWrap}`}>
              <Icon className="h-5 w-5" aria-hidden />
            </div>
          ) : null}
          <ChevronRight
            className="h-4 w-4 text-gray-300 group-hover:text-gray-500 transition"
            aria-hidden
          />
        </div>
      </div>
      <div
        className={`relative z-10 pointer-events-none mt-4 -mx-5 -mb-5 px-5 py-2.5 border-t text-xs font-medium flex items-center justify-between gap-2 ${t.footer}`}
      >
        <span className="truncate">{footerHint || 'Action recommandée'}</span>
        <span className="shrink-0 inline-flex items-center gap-0.5">
          {footerLabel}
          <ChevronRight className="h-3.5 w-3.5" aria-hidden />
        </span>
      </div>
      <button
        type="button"
        onClick={onOpen}
        className={`absolute inset-0 z-0 rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${t.ring}`}
        aria-label={`${title} — ouvrir le détail`}
      />
    </div>
  );
}
